import React from 'react';
import { css } from '@emotion/react';
import IconArrow from '../../images/svg/arrow-forward.svg';
import { colors } from '../primitives/colors';
import Image from '../primitives/image';
import Link from '../primitives/link';
import { truncate as truncateText, truncateHTML } from '../utils/truncate';
import { ArticleResult } from './types';

const styles = {
    articleCardWrapper: css({
        position: 'relative',
        padding: '8px',
        paddingBottom: '20px',
        height: '100%',
        backgroundColor: colors.shadesWhite,
        color: colors.shadesBlack,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    }),
    gridContainer: css({
        display: 'grid',
        gridTemplateRows: 'auto auto 73px',
        paddingLeft: '16px',
        paddingRight: '16px',
    }),
    title: css({
        color: colors.primaryOrange,
        marginBottom: '12px',
        fontSize: '20px',
        lineHeight: '28px',
        letterSpacing: '0px',
    }),
    link: css({
        '@media (min-width: 1024px)': {
            display: 'inline',
        },
    }),
    horizontalLineWrapper: css({
        display: 'flex',
        marginBottom: '16px',
    }),
    horizontalLine: css({
        display: 'inline-block',
        width: '48px',
        height: '1px',
        backgroundColor: colors.primaryGreen,
    }),
    readMoreWrapper: css({
        display: 'flex',
        marginTop: '16px',
        alignItems: 'center',
        fontWeight: 600,
    }),
    readMoreArrowIcon: css({
        marginLeft: '4px',
    }),
    contentStyles: css({
        overflowY: 'hidden',
        marginBottom: '8px',
        fontSize: '18px',
        lineHeight: 1.25,
    }),
    cardImageContainer: css({
        display: 'block',
        position: 'relative',
        marginBottom: '4px',
        '::before': {
            display: 'block',
            paddingTop: '86%',
            content: "''",
        },
    }),
    cardImage: css({
        display: 'block',
        objectFit: 'cover',
        objectPosition: 'center',
        marginBottom: '16px',
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        clipPath: 'polygon(0% 0%, 100% 0, 100% 75%, 50% 97%, 0 75%)',
    }),
};

interface ArticleCardProps {
    article: ArticleResult
    htmlContent?: boolean
}

const ArticleCard = ({ article, htmlContent = true }: ArticleCardProps): JSX.Element => {
    const { featuredImage, title, content, url } = article;
    const contentMaxLength = 90;
    const contentTruncateOptions = {
        forceEllipsis: true,
        noEllipsisIfEmpty: true,
    };

    return (
        <div
            css={styles.articleCardWrapper}
            key={title}
        >
            {featuredImage?.url && (
                <Link css={styles.cardImageContainer} to={url}>
                    <Image css={styles.cardImage} image={featuredImage} alt={featuredImage.title || ''} />
                </Link>
            )}
            <div css={styles.gridContainer}>
                <h3 css={styles.title}>
                    <Link css={styles.link} to={url}>
                        {title}
                    </Link>
                </h3>
                <div css={styles.horizontalLineWrapper}>
                    <span css={styles.horizontalLine} />
                </div>
                {htmlContent ? (
                    <div
                        css={styles.contentStyles}
                        /* eslint-disable-next-line react/no-danger */
                        dangerouslySetInnerHTML={{
                            __html: truncateHTML(content, contentMaxLength),
                        }}
                    />
                ) : (
                    <div css={styles.contentStyles}>
                        {truncateText(content, contentMaxLength, contentTruncateOptions)}
                    </div>
                )}
                <div css={styles.readMoreWrapper}>
                    <Link className="hover-default" to={url}>
                        Read More
                    </Link>
                    <IconArrow css={styles.readMoreArrowIcon} />
                </div>
            </div>
        </div>
    );
};

export default ArticleCard;
